import { ChangeEventHandler, useState } from 'react';
import { useToastContext } from '@/contexts/toastContext';
import useApiSubmit from '@/hooks/useApiSubmit';
import InterviewRecordingUploaderComponent, {
  InterviewRecordingUploaderComponentProps,
} from './InterviewRecordingUploader.component';
import interviewsServices, {
  DeleteInterviewRecordingType,
  UploadInterviewRecordingType,
} from '../../services/interviews';

type InterviewRecordingUploaderProps = Pick<
  InterviewRecordingUploaderComponentProps,
  'name' | 'className' | 'recordingUrl' | 'label'
> & {
  interviewId: string;
  onInterviewUpdate?: () => void;
};

const InterviewRecordingUploader = ({
  interviewId,
  onInterviewUpdate,
  ...componentProps
}: InterviewRecordingUploaderProps): JSX.Element => {
  const toast = useToastContext();
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState<boolean>(false);
  const [uploadProgress, setUploadProgress] = useState<number>(0);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [showErrorModal, setShowErrorModal] = useState<boolean>(false);

  const uploadInterviewRecordingMutation = useApiSubmit<unknown, UploadInterviewRecordingType>(
    interviewsServices.uploadInterviewRecording
  );

  const deleteInterviewRecordingMutation = useApiSubmit<unknown, DeleteInterviewRecordingType>(
    interviewsServices.deleteInterviewRecording
  );

  const handleDeleteConfirm = async (): Promise<void> => {
    try {
      await deleteInterviewRecordingMutation.mutateAsync({
        interviewId,
      });

      onInterviewUpdate?.();
    } catch (error) {
      toast.showErrorFeedback('There was a problem deleting the recording.');
    } finally {
      setShowDeleteConfirmation(false);
    }
  };

  const handleInputChange: ChangeEventHandler<HTMLInputElement> = async (event) => {
    const file = event.target.files?.[0];

    if (file) {
      setUploadProgress(0);
      setUploadError(null);
      setShowErrorModal(false);

      try {
        await uploadInterviewRecordingMutation.mutateAsync({
          interviewId,
          recordingFile: file,
          onUploadProgress: (progressEvent) => {
            const percentCompleted = Math.round(
              (progressEvent.loaded * 100) / (progressEvent.total || 1)
            );
            setUploadProgress(percentCompleted);
          },
        });

        setUploadProgress(100);
        onInterviewUpdate?.();
      } catch (error: any) {
        const errorMessage =
          error?.response?.data?.message ||
          error?.message ||
          'There was a problem uploading the recording. Please try again.';
        setUploadError(errorMessage);
        setShowErrorModal(true);
        setUploadProgress(0);
      }
    }
  };

  return (
    <InterviewRecordingUploaderComponent
      isUploading={uploadInterviewRecordingMutation.isLoading}
      isDeleting={deleteInterviewRecordingMutation.isLoading}
      uploadProgress={uploadProgress}
      uploadError={uploadError}
      showErrorModal={showErrorModal}
      onFileChange={handleInputChange}
      showDeleteConfirmation={showDeleteConfirmation}
      setShowDeleteConfirmation={setShowDeleteConfirmation}
      onDeleteConfirm={handleDeleteConfirm}
      onCloseErrorModal={() => {
        setShowErrorModal(false);
        setUploadError(null);
      }}
      {...componentProps}
    />
  );
};

export default InterviewRecordingUploader;
